import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isArabicNumber, toEnglishNumber } from "../utils/arabicConversions";
import { searchAPI } from "../axios/search-axios";
import { toast } from "react-toastify";
import { SEMANTIC_SEARCH_FEATURE_ENABLED } from "../config/featureFlags";

const TOAST_IDS = {
  invalidYear: "left-pane-invalid-year",
  noResults: "left-pane-no-results",
};

const BASE_TOAST_OPTIONS = {
  position: "top-center",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: false,
};

const useLeftPane = ({
  setShow,
  setSearchResults,
  setSearchResultSets,
  setAnalytics,
  setCurrentPage,
  setAnalyticsModalOpen,
  setLoading,
  filtersResetToken,
  setRagSummary,
}) => {
    const [dateVisible, setDateVisible] = useState(false);
  const [locationVisible, setLocationVisible] = useState(false);
  const [collectionVisible, setCollectionVisible] = useState(false);
  const [sourceVisible, setSourceVisible] = useState(false);

  const toggleDate = () => {
    setDateVisible(!dateVisible);
  };

  const toggleLocation = () => {
    setLocationVisible(!locationVisible);
  };

  const toggleCollection = () => {
    setCollectionVisible(!collectionVisible);
  };

  const toggleSource = () => {
    setSourceVisible(!sourceVisible);
  };

  const readSemanticEnabled = () => {
    if (!SEMANTIC_SEARCH_FEATURE_ENABLED) {
      return false;
    }
    const storedValue = localStorage.getItem("semanticSearchEnabled");
    return storedValue ? JSON.parse(storedValue) : false;
  };

  const [fromYear, setFromYear] = useState(null);
  const [toYear, setToYear] = useState(null);

  const lastSubmittedFiltersRef = useRef(null);

  const handleFromYearChange = (event) => {
    let numericValue = "";
    const inputValue = event.target.value;
    if (isArabicNumber(inputValue)) {
      numericValue = toEnglishNumber(inputValue);
    } else {
      numericValue = inputValue.replace(/\D/g, "");
    }

    const truncatedValue = numericValue.slice(0, 4);
    setFromYear(truncatedValue);

    let updatedFilters;
    if (truncatedValue !== "") {
      updatedFilters = { ...filters, from_year: truncatedValue };
    } else {
      const { from_year, ...rest } = filters;
      updatedFilters = rest;
    }
    setFilters(updatedFilters);
    localStorage.setItem("filters", JSON.stringify(updatedFilters));
  };

  const handleToYearChange = (event) => {
    let numericValue = "";
    const inputValue = event.target.value;
    if (isArabicNumber(inputValue)) {
      numericValue = toEnglishNumber(inputValue);
    } else {
      numericValue = inputValue.replace(/\D/g, "");
    }

    const truncatedValue = numericValue.slice(0, 4);
    setToYear(truncatedValue);

    let updatedFilters;
    if (truncatedValue !== "") {
      updatedFilters = { ...filters, to_year: truncatedValue };
    } else {
      const { to_year, ...rest } = filters;
      updatedFilters = rest;
    }
    setFilters(updatedFilters);
    localStorage.setItem("filters", JSON.stringify(updatedFilters));
  };

  const lang = localStorage.getItem("lang");
  const collNameKey = lang === "ar" ? "coll_name_arabic" : "coll_name";
  const collLocationKey =
    lang === "ar" ? "coll_location_arabic" : "coll_location";
  const collSourceKey = lang === "ar" ? "coll_source_arabic" : "coll_source";

  const getInitialFilters = () => {
    const baseFilters = {
      search_term: localStorage.getItem("searchTerm")
        ? localStorage.getItem("searchTerm").replace(/^"(.*)"$/, "$1")
        : "",
      match_type: localStorage.getItem("matchType")
        ? localStorage.getItem("matchType").replace(/^"(.*)"$/, "$1")
        : "any",
      sort_type: "desc",
      [collNameKey]: [],
      [collLocationKey]: [],
      [collSourceKey]: [],
    };

    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      baseFilters.semantic_search_enabled = readSemanticEnabled();
    }

    return baseFilters;
  };

  const [filters, setFilters] = useState(getInitialFilters());

  useEffect(() => {
    if (filtersResetToken === undefined) return;
    const initialFilters = getInitialFilters();
    setFilters(initialFilters);
    localStorage.setItem("filters", JSON.stringify(initialFilters));
    setFromYear(null);
    setToYear(null);
    lastSubmittedFiltersRef.current = JSON.stringify(initialFilters);
  }, [filtersResetToken]);

  const handleCollectionCheckboxChange = (event, collName) => {
    const isChecked = event.target.checked;

    const currentValues = Array.isArray(filters[collNameKey])
      ? [...filters[collNameKey]]
      : [];
    let nextValues;
    if (isChecked) {
      nextValues = Array.from(new Set([...currentValues, collName]));
    } else {
      nextValues = currentValues.filter((x) => x !== collName);
    }
    const updatedFilters = {
      ...filters,
      [collNameKey]: nextValues,
    };
    setFilters(updatedFilters);
    localStorage.setItem("filters", JSON.stringify(updatedFilters));
  };

  const handleSourceCheckboxChange = (event, collSource) => {
    const isChecked = event.target.checked;

    const currentValues = Array.isArray(filters[collSourceKey])
      ? [...filters[collSourceKey]]
      : [];
    let nextValues;
    if (isChecked) {
      nextValues = Array.from(new Set([...currentValues, collSource]));
    } else {
      nextValues = currentValues.filter((x) => x !== collSource);
    }
    const updatedFilters = {
      ...filters,
      [collSourceKey]: nextValues,
    };
    setFilters(updatedFilters);
    localStorage.setItem("filters", JSON.stringify(updatedFilters));
  };

  const handleLocationCheckboxChange = (event, collLocation) => {
    const isChecked = event.target.checked;

    const currentValues = Array.isArray(filters[collLocationKey])
      ? [...filters[collLocationKey]]
      : [];
    let nextValues;
    if (isChecked) {
      nextValues = Array.from(new Set([...currentValues, collLocation]));
    } else {
      nextValues = currentValues.filter((x) => x !== collLocation);
    }
    const updatedFilters = {
      ...filters,
      [collLocationKey]: nextValues,
    };
    setFilters(updatedFilters);
    localStorage.setItem("filters", JSON.stringify(updatedFilters));
  };

  const navigate = useNavigate();

  const submitFilters = async () => {
    // Stringify the keys of the filters object
    // let filtersStringified = JSON.stringify(filters);
    // Check the filters object. If coll_name is empty array, remove it from the object.
    // Same for coll_location and coll_source

    setLoading(true);

    if (fromYear && toYear && fromYear > toYear) {
      if (lang !== "ar") {
        toast.error("Please enter a valid range of years", {
          ...BASE_TOAST_OPTIONS,
          toastId: TOAST_IDS.invalidYear,
        });
      } else {
        toast.error("الرجاء إدخال نطاق سنوات صالح", {
          ...BASE_TOAST_OPTIONS,
          rtl: true,
          toastId: TOAST_IDS.invalidYear,
        });
      }
      setLoading(false);
      return;
    }

    const currentMatchType = localStorage.getItem("matchType")
      ? localStorage.getItem("matchType").replace(/^"(.*)"$/, "$1")
      : "any";
    const filtersWithSemantic = {
      ...filters,
      match_type: currentMatchType,
    };

    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      filtersWithSemantic.semantic_search_enabled = readSemanticEnabled();
    }

    const serializedFilters = JSON.stringify(filtersWithSemantic);
    if (serializedFilters === lastSubmittedFiltersRef.current) {
      setLoading(false);
      if (typeof setShow === "function") setShow(false);
      return;
    }

    // persist the filters state (including semantic flag)
    setFilters(filtersWithSemantic);
    localStorage.setItem("filters", serializedFilters);

    let res = await searchAPI.put("", filtersWithSemantic, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    let results = res.data.docs;
    if (results.length === 0) {
      if (lang !== "ar") {
        toast.warn("No results found. Please expand your filters", {
          ...BASE_TOAST_OPTIONS,
          toastId: TOAST_IDS.noResults,
        });
      } else {
        toast.warn("لم يتم العثور على نتائج. يرجى توسيع الفلاتر الخاصة بك", {
          ...BASE_TOAST_OPTIONS,
          rtl: true,
          toastId: TOAST_IDS.noResults,
        });
      }
      setLoading(false);
      return;
    }

    setSearchResults(res.data.docs);
    setSearchResultSets((prevSearchResultSets) => ({
      [1]: res.data.docs,
    }));
    setAnalytics(res.data.analytics);
    setCurrentPage(1);

    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      if (setRagSummary) {
        setRagSummary(res.data.rag || "");
      }
      localStorage.setItem("ragSummary", JSON.stringify(res.data.rag || ""));
    }

    const navigationState = {
      searchResults: res.data.docs,
      pageNumber: 1,
      searchString: localStorage.getItem("searchTerm")
        ? localStorage.getItem("searchTerm").replace(/^"(.*)"$/, "$1")
        : "",
    };

    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      navigationState.rag = res.data.rag || "";
    }

    //Navigate to search results page
    navigate("/search-results", {
      state: navigationState,
    });

    // remember last submitted filters to avoid duplicate requests
    lastSubmittedFiltersRef.current = serializedFilters;

    // Update filters state to clear selected checkboxes for the UI
    let newFilters = {
      ...filtersWithSemantic,
      [collNameKey]: [],
      [collLocationKey]: [],
      [collSourceKey]: [],
    };
    setFilters(newFilters);
    localStorage.setItem("filters", JSON.stringify(newFilters));

    // Uncheck all checkboxes in the UI
    let checkboxes = document.querySelectorAll("input[type=checkbox]");
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    setLoading(false);

    if (typeof setShow === "function") setShow(false);
  };

  const clearFilters = async () => {
    setLoading(true);

    //Clear the filters object

    const initialFilters = getInitialFilters();
    setFilters(initialFilters);
    localStorage.setItem("filters", JSON.stringify(initialFilters));

    //Uncheck all checkboxes
    let checkboxes = document.querySelectorAll("input[type=checkbox]");
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    setFromYear("");
    setToYear("");

    //Send empty filters to backend and update everything
    const serializedInitial = JSON.stringify(initialFilters);
    if (serializedInitial === lastSubmittedFiltersRef.current) {
      setLoading(false);
      if (typeof setShow === "function") setShow(false);
      return;
    }
    let res = await searchAPI.put("", initialFilters);
    setSearchResults(res.data.docs);
    setSearchResultSets((prevSearchResultSets) => ({
      [1]: res.data.docs,
    }));
    setAnalytics(res.data.analytics);
    setCurrentPage(1);
    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      if (setRagSummary) {
        setRagSummary(res.data.rag || "");
      }
      localStorage.setItem("ragSummary", JSON.stringify(res.data.rag || ""));
    }

    const navigationState = {
      searchResults: res.data.docs,
      pageNumber: 1,
      searchString: localStorage
        .getItem("searchTerm")
        .replace(/^"(.*)"$/, "$1"),
    };

    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      navigationState.rag = res.data.rag || "";
    }

    navigate("/search-results", {
      state: navigationState,
    });
    lastSubmittedFiltersRef.current = serializedInitial;
    setLoading(false);
    if (typeof setShow === "function") setShow(false);
    // window.location.reload(true);
    // forceUpdate();
  };

  const [showMoreLocation, setShowMoreLocation] = useState(false);
  const [showMoreCollection, setShowMoreCollection] = useState(false);
  const [showMoreSource, setShowMoreSource] = useState(false);

  const toggleMoreLocation = () => {
    setShowMoreLocation(!showMoreLocation);
  };

  const toggleMoreCollection = () => {
    setShowMoreCollection(!showMoreCollection);
  };

  const toggleMoreSource = () => {
    setShowMoreSource(!showMoreSource);
  };

  const handleAnalyticsClick = () => {
    setAnalyticsModalOpen(true);
  };

  return {
    dateVisible,
    setDateVisible,
    locationVisible,
    setLocationVisible,
    collectionVisible,
    setCollectionVisible,
    sourceVisible,
    setSourceVisible,
    fromYear,
    setFromYear,
    toYear,
    setToYear,
    filters,
    setFilters,
    toggleDate,
    toggleLocation,
    toggleCollection,
    toggleSource,
    handleFromYearChange,
    handleToYearChange,
    handleCollectionCheckboxChange,
    handleSourceCheckboxChange,
    handleLocationCheckboxChange,
    submitFilters,
    clearFilters,
    showMoreLocation,
    setShowMoreLocation,
    showMoreCollection,
    setShowMoreCollection,
    showMoreSource,
    setShowMoreSource,
    handleAnalyticsClick,
    toggleMoreLocation,
    toggleMoreCollection,
    toggleMoreSource,
  };
};

export default useLeftPane;
